'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';

/**
 * Boot scene — shared by the first-load splash below and the route-transition
 * fallback (app/[locale]/loading.tsx): one of our courtyard backgrounds washed
 * with a sand tint so it blends into the page, hanging vines in the top
 * corners, and a single twinkling gold star at the centre. The star and tint
 * paint instantly (pure CSS/SVG); the artwork fades in whenever it arrives.
 */
export function SplashScene() {
  return (
    <>
      {/* backdrop art + sand tint that blends it into the page background */}
      <Image
        src="/stack-bg-frame-cream.webp"
        alt=""
        aria-hidden
        fill
        priority
        sizes="100vw"
        className="select-none object-cover opacity-35"
      />
      <div aria-hidden className="absolute inset-0 bg-background/60" />
      {/* hanging vines, mirrored pair — same dressing as the gates section */}
      <Image
        src="/fg-vine.webp"
        alt=""
        aria-hidden
        width={514}
        height={1351}
        priority
        className="absolute end-0 top-0 w-24 select-none rtl:-scale-x-100 sm:w-36"
      />
      <Image
        src="/fg-vine.webp"
        alt=""
        aria-hidden
        width={514}
        height={1351}
        priority
        className="absolute start-0 top-0 w-24 select-none ltr:-scale-x-100 sm:w-36"
      />
      {/* the star — the brand's four-point spark, twinkling */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden
        className="relative size-12 fill-current text-brand-gold motion-safe:[animation:twinkle_1.6s_ease-in-out_infinite]"
      >
        <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
      </svg>
    </>
  );
}

/**
 * Boot splash — covers the first paint so a slow start reads as intentional.
 * Server-rendered (visible before hydration), dismissed two frames after
 * hydration, fully unmounted after the fade. A pure-CSS `splash-hide`
 * animation clears it at 2.5s even if JavaScript never runs. Client-side
 * navigations never re-show it.
 */
export function SplashScreen() {
  const [gone, setGone] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    // Two frames after hydration: fonts/styles applied and the hero shell
    // painted underneath — safe to reveal.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setGone(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  if (removed) return null;

  return (
    <div
      aria-hidden
      onTransitionEnd={() => gone && setRemoved(true)}
      className={cn(
        'fixed inset-0 z-[100] grid place-items-center overflow-hidden bg-background transition-opacity duration-300 ease-out',
        'motion-safe:[animation:splash-hide_0.4s_ease_2.5s_forwards] motion-reduce:[animation:splash-hide_0.4s_ease_1s_forwards]',
        gone && 'pointer-events-none opacity-0',
      )}
    >
      <SplashScene />
    </div>
  );
}
